/**
 * Standardized Date Formatting Utilities for Dexpert HRMS
 * Enforces DD-MM-YYYY format throughout the application.
 */

/**
 * Format any date value into DD-MM-YYYY
 * @param {string|Date|number} value - Date string, Date object, or timestamp
 * @param {string} [fallback='—'] - Fallback string if value is null/invalid
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDate = (value, fallback = '—') => {
  const fallbackStr = typeof fallback === 'string' ? fallback : '—';
  if (value === null || value === undefined || value === '') {
    return fallbackStr;
  }

  // Handle strings directly to prevent timezone skew (e.g. UTC midnight shifting date by 1 day)
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;

    // Check if already in DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
      return trimmed;
    }

    // Check if in DD/MM/YYYY or DD.MM.YYYY
    if (/^\d{2}[\/\.]\d{2}[\/\.]\d{4}$/.test(trimmed)) {
      return trimmed.replace(/[\/\.]/g, '-');
    }

    // Check if pure YYYY-MM-DD or YYYY/MM/DD or YYYY.MM.DD
    const ymdMatch = trimmed.match(/^(\d{4})[-\/\.](\d{2})[-\/\.](\d{2})$/);
    if (ymdMatch) {
      const [, year, month, day] = ymdMatch;
      return `${day}-${month}-${year}`;
    }

    // Check if YYYY-MM-DD followed by time (e.g. 2026-10-22T00:00:00 or with timezone)
    const isoPrefixMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoPrefixMatch) {
      // If pure UTC date without non-zero time (like T00:00:00Z)
      if (trimmed.includes('T00:00:00') || trimmed.includes(' 00:00:00')) {
        const [, year, month, day] = isoPrefixMatch;
        return `${day}-${month}-${year}`;
      }
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return fallbackStr;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Format date with time into DD-MM-YYYY HH:mm
 * @param {string|Date|number} value
 * @param {string} [fallback='—']
 * @returns {string}
 */
export const formatDateTime = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return fallback;

  const datePart = formatDate(date, fallback);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${datePart} ${hours}:${minutes}`;
};

/**
 * Format date for HTML5 <input type="date"> (requires YYYY-MM-DD)
 * @param {string|Date} value
 * @returns {string}
 */
export const formatDateForInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const dmyMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      return `${year}-${month}-${day}`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default formatDate;
